import { departments as allDepartments, menus as allMenus } from '../config/menuConfig';
import { pathFor } from '../config/routeMap';

// Build a flat searchable list of items from the hierarchical menu structure.
// Accepts optional filtered departments/menus for access-controlled views.
export function buildSearchIndex(departments, menus) {
  departments = departments || allDepartments;
  menus = menus || allMenus;
  const items = [];
  const push = (entry) => items.push(entry);

  // Core command actions (non-navigation) – id prefix 'cmd:' so we can distinguish
  const commandActions = [
    { id:'cmd:toggle-compact', label:'Toggle Compact Layout', fullLabel:'Action › Toggle Compact Layout', type:'action', tokens:tokenize('toggle compact layout ui view') },
    { id:'cmd:start-trust-scan', label:'Start Trust Tech Scan', fullLabel:'Action › Start Trust Tech Scan', type:'action', tokens:tokenize('start trust tech scan run analysis') },
    { id:'cmd:start-report', label:'Generate Trust Report', fullLabel:'Action › Generate Trust Report', type:'action', tokens:tokenize('generate trust report create report') },
    { id:'cmd:open-notifications', label:'Open Notifications', fullLabel:'Action › Open Notifications', type:'action', tokens:tokenize('open notifications bell messages alerts') },
    { id:'cmd:open-profile', label:'Open Profile Menu', fullLabel:'Action › Open Profile Menu', type:'action', tokens:tokenize('open profile account menu user') },
  { id:'cmd:clear-recents', label:'Clear Recent Commands', fullLabel:'Action › Clear Recent Commands', type:'action', tokens:tokenize('clear recent commands history') },
  { id:'cmd:go-dashboard', label:'Go to Dashboard', fullLabel:'Action › Go to Dashboard', type:'action', tokens:tokenize('go dashboard home main trust tech studio') },
  { id:'cmd:open-todos', label:'Open Todos', fullLabel:'Action › Open Todos', type:'action', tokens:tokenize('open todos tasks productivity list') }
  ];
  commandActions.forEach(push);

  departments.forEach((dept) => {
    const rootPath = pathFor(dept);
    push({
      id: `dept:${dept}`,
      label: dept,
      fullLabel: dept,
      path: rootPath,
      type: 'department',
      tokens: tokenize(dept)
    });

    const walk = (obj, trail = []) => {
      Object.entries(obj).forEach(([label, children]) => {
        const currentTrail = [...trail, label];
        const pathSegments = currentTrail.map(t => t === 'Overview' ? '' : t).filter(Boolean);
        const path = pathFor(dept, ...pathSegments);
        push({
          id: `item:${dept}:${currentTrail.join('>')}`,
            label,
            fullLabel: `${dept} › ${currentTrail.join(' › ')}`,
            path,
            type: 'module',
            tokens: tokenize(`${dept} ${currentTrail.join(' ')}`)
        });

        if (Array.isArray(children)) {
          children.forEach(child => {
            if (typeof child === 'string') {
              const leafTrail = [...currentTrail, child];
              const leafPath = pathFor(dept, ...leafTrail);
              push({
                id: `leaf:${dept}:${leafTrail.join('>')}`,
                label: child,
                fullLabel: `${dept} › ${leafTrail.join(' › ')}`,
                path: leafPath,
                type: 'page',
                tokens: tokenize(`${dept} ${leafTrail.join(' ')}`)
              });
            } else if (child && typeof child === 'object') {
              const groupTrail = [...currentTrail, child.label];
              const groupPath = pathFor(dept, ...groupTrail);
              push({
                id: `group:${dept}:${groupTrail.join('>')}`,
                label: child.label,
                fullLabel: `${dept} › ${groupTrail.join(' › ')}`,
                path: groupPath,
                type: 'group',
                tokens: tokenize(`${dept} ${groupTrail.join(' ')}`)
              });
              if (Array.isArray(child.children)) {
                child.children.forEach(leaf => {
                  const leafTrail = [...groupTrail, leaf];
                  const leafPath = pathFor(dept, ...leafTrail);
                  push({
                    id: `leaf:${dept}:${leafTrail.join('>')}`,
                    label: leaf,
                    fullLabel: `${dept} › ${leafTrail.join(' › ')}`,
                    path: leafPath,
                    type: 'page',
                    tokens: tokenize(`${dept} ${leafTrail.join(' ')}`)
                  });
                });
              }
            }
          });
        }
      });
    };

    const content = menus[dept];
    if (content) walk(content, []);
  });

  return items;
}

function tokenize(str) {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
}

// Simple fuzzy scoring: higher = better.
// - Exact substring match +10
// - Token starts-with +4
// - Presence +1 each
export function scoreQuery(item, queryTokens, rawQuery) {
  if (!rawQuery) return 0;
  const labelLower = item.fullLabel.toLowerCase();
  let score = 0;
  // slight base preference for actions to surface quick commands
  if(item.type === 'action') score += 2;
  if (labelLower.includes(rawQuery.toLowerCase())) score += 10;
  queryTokens.forEach(t => {
    if (item.tokens.includes(t)) score += 1;
    // starts-with in any token
    if (item.tokens.some(tok => tok.startsWith(t))) score += 4;
  });
  return score;
}

export function searchIndex(index, rawQuery, limit = 15) {
  const q = rawQuery.trim();
  if (!q) return [];
  const tokens = tokenize(q);
  return index
    .map(item => ({ item, score: scoreQuery(item, tokens, q) }))
    .filter(r => r.score > 0)
    .sort((a,b) => b.score - a.score)
    .slice(0, limit)
    .map(r => r.item);
}
